import React, { useState, useEffect } from 'react';
import {
  DocumentRecord,
  DocumentCategory,
  DocumentsDashboardKpis,
  DocumentChangeRequest,
  DocumentAcknowledgement,
} from './types';
import { documentsService } from './documentsService';
import { DocumentsMetricsCards } from './components/DocumentsMetricsCards';
import { DocumentsTable } from './components/DocumentsTable';
import { DocumentCreateModal } from './components/DocumentCreateModal';
import { DocumentDetailModal } from './components/DocumentDetailModal';
import { DocumentComplianceAuditTab } from './components/DocumentComplianceAuditTab';

export const DocumentsPage: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'register' | 'changes' | 'acks' | 'compliance'>('register');
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [kpis, setKpis] = useState<DocumentsDashboardKpis | null>(null);
  const [changeRequests, setChangeRequests] = useState<DocumentChangeRequest[]>([]);
  const [acknowledgements, setAcknowledgements] = useState<DocumentAcknowledgement[]>([]);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Modals
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentRecord | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, [selectedCategory, selectedStatus, searchTerm]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kpiData, catData, docData, crData, ackData] = await Promise.all([
        documentsService.getDashboardKpis(),
        documentsService.getCategories(),
        documentsService.getDocuments({
          category_id: selectedCategory || undefined,
          status: selectedStatus || undefined,
          search: searchTerm || undefined,
        }),
        documentsService.getChangeRequests(),
        documentsService.getAcknowledgements(),
      ]);
      setKpis(kpiData);
      setCategories(catData);
      setDocuments(docData);
      setChangeRequests(crData);
      setAcknowledgements(ackData);
    } catch (err) {
      console.error('Error loading documents module data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDoc = (doc: DocumentRecord) => {
    setSelectedDoc(doc);
    setIsDetailOpen(true);
  };

  const handleCreateDocument = async (data: any) => {
    await documentsService.createDocument(data);
    await loadAll();
  };

  const handleSignAck = async (ackId: string, userId: string) => {
    try {
      await documentsService.recordAcknowledgement(ackId, userId);
      await loadAll();
    } catch (e: any) {
      alert(e.message || 'Tebellug onaylanirken hata olustu');
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="page-header-banner">
        <div className="badge">QUALITY & COMPLIANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Controlled Documents Management</h1>
            <p>EASA ORO.GEN.200 & ICAO SMS controlled operations manuals, SOPs, Separation of Duties (SoD), and employee distribution acknowledgments.</p>
          </div>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + New Controlled Document
          </button>
        </div>
      </div>

      {/* KPI Dashboard */}
      <DocumentsMetricsCards kpis={kpis} />

      {/* Sub Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveSubTab('register')}
          className={`tab-btn ${activeSubTab === 'register' ? 'active' : ''}`}
        >
          Master Document Register
        </button>
        <button
          onClick={() => setActiveSubTab('changes')}
          className={`tab-btn ${activeSubTab === 'changes' ? 'active' : ''}`}
        >
          Change Requests ({changeRequests.length})
        </button>
        <button
          onClick={() => setActiveSubTab('acks')}
          className={`tab-btn ${activeSubTab === 'acks' ? 'active' : ''}`}
        >
          Employee Acknowledgements ({acknowledgements.length})
        </button>
        <button
          onClick={() => setActiveSubTab('compliance')}
          className={`tab-btn ${activeSubTab === 'compliance' ? 'active' : ''}`}
        >
          📊 Read & Sign Compliance Audit
        </button>
      </div>

      {/* Content based on sub-tab */}
      {activeSubTab === 'register' && (
        <div>
          {/* Filters Bar */}
          <div className="card" style={{ padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: '260px' }}>
              <input
                type="text"
                placeholder="Search by Document No, Title, or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                }}
              />
            </div>
            <div style={{ width: '200px' }}>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ width: '180px' }}>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                }}
              >
                <option value="">All Statuses</option>
                <option value="PUBLISHED">Published</option>
                <option value="PENDING_APPROVAL">Pending Approval</option>
                <option value="PENDING_REVIEW">In Review</option>
                <option value="DRAFT">Draft</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <DocumentsTable
            documents={documents}
            categories={categories}
            onSelect={handleSelectDoc}
            selectedDocId={selectedDoc?.id}
          />
        </div>
      )}

      {activeSubTab === 'changes' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: '13px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Active Document Change & Revision Requests
          </div>
          <div className="table-container" style={{ border: 'none', boxShadow: 'none', margin: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Request No</th>
                  <th>Target Document</th>
                  <th>Title & Reason</th>
                  <th>Source</th>
                  <th>Requester</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {changeRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      No change requests registered.
                    </td>
                  </tr>
                ) : (
                  changeRequests.map((cr) => (
                    <tr key={cr.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>{cr.request_number}</td>
                      <td style={{ fontWeight: 600 }}>{cr.target_document_number}</td>
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{cr.title}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{cr.reason}</div>
                      </td>
                      <td>
                        <span className="badge-pill badge-neutral">{cr.source_type}</span>
                      </td>
                      <td>{cr.requester_name}</td>
                      <td>
                        <span className="badge-pill badge-info">{cr.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSubTab === 'acks' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, fontSize: '13px', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Mandatory Personnel Acknowledgement & Read Tracking Register
          </div>
          <div className="table-container" style={{ border: 'none', boxShadow: 'none', margin: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Personnel</th>
                  <th>Department</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {acknowledgements.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                      No acknowledgement records found.
                    </td>
                  </tr>
                ) : (
                  acknowledgements.map((ack) => (
                    <tr key={ack.id}>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{ack.user_name}</td>
                      <td>{ack.user_department || 'Operations'}</td>
                      <td>{ack.due_date?.slice(0, 10)}</td>
                      <td>
                        {ack.status === 'ACKNOWLEDGED' ? (
                          <span className="badge-pill badge-success">
                            ✓ Acknowledged ({ack.acknowledged_at?.slice(0, 10)})
                          </span>
                        ) : (
                          <span className="badge-pill badge-warning">Pending</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {ack.status !== 'ACKNOWLEDGED' && (
                          <button
                            onClick={() => handleSignAck(ack.id, ack.user_id)}
                            className="btn-primary"
                            style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                          >
                            Acknowledge & Sign
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSubTab === 'compliance' && (
        <DocumentComplianceAuditTab documents={documents} />
      )}

      {/* Modals */}
      <DocumentCreateModal
        categories={categories}
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreateDocument}
      />

      {selectedDoc && (
        <DocumentDetailModal
          document={selectedDoc}
          isOpen={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          onRefresh={loadAll}
        />
      )}
    </div>
  );
};
